// BuyerHub — dedicated buyer-side experience with the 11-tab layout Erika
// wants. Buyers with `active_side === "buy"` land here instead of the
// seller dashboard. Every tab pulls from PorchLight's existing endpoints
// (site_content, buyer timeline, forms, messages, referrals) so admins keep
// editing everything in one place at /admin/content.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, Calculator, ClipboardList, Users, Calendar, FileText, MessageCircle, Send, BookOpen, FolderArchive, Phone, Mail, ArrowRight, Heart, Home, Plus, Trash2, X, Camera, Pencil } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useContent, DEFAULT_CONTENT } from "../lib/content";
import { useBranding } from "../components/Layout";
import { useBuyerContent } from "../lib/buyerContent";
import Forms from "./Forms";
import MortgageCalc from "./MortgageCalc";

const TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "process",   label: "Buyer Process" },
  { key: "lender",    label: "Lender Info" },
  { key: "calc",      label: "Calculators" },
  { key: "vendors",   label: "Vendors" },
  { key: "resources", label: "Resources" },
  { key: "documents", label: "Documents" },
  { key: "forms",     label: "Forms" },
  { key: "ask", label: "__ASK__" },
  { key: "refer",     label: "Refer a Friend" },
  { key: "timeline",  label: "Contract Timeline" },
];

export default function BuyerHub() {
  const { agent_first_name: agentFirst } = useBranding();
  const [tab, setTab] = useState("dashboard");
  const goto = (k) => { setTab(k); window.scrollTo({ top: 0, behavior: "smooth" }); };
  return (
    <div className="page-fade" style={{ minHeight: "100vh" }}>
      <div style={{ background: "var(--bg)", position: "sticky", top: 65, zIndex: 10, borderBottom: "1px solid var(--border)" }}>
        <div className="container-x">
          <div className="tabs-bar" data-testid="buyer-tabs" style={{ display: "flex", gap: 28, overflowX: "auto" }}>
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className="btn-ghost"
                style={{
                  padding: "16px 0", fontSize: 13, fontWeight: 500, letterSpacing: "0.04em",
                  color: tab === t.key ? "var(--primary)" : "var(--text-muted)",
                  borderBottom: `2px solid ${tab === t.key ? "var(--primary)" : "transparent"}`,
                  background: "transparent", whiteSpace: "nowrap", borderRadius: 0,
                }}
              >{t.label === "__ASK__" ? `Ask ${agentFirst}` : t.label}</button>
            ))}
          </div>
        </div>
      </div>
      <main className="container-x" style={{ padding: "48px 0 96px" }}>
        {tab === "dashboard" && <DashboardTab goto={goto} />}
        {tab === "process"   && <ProcessTab />}
        {tab === "lender"    && <LenderInfoTab />}
        {tab === "calc"      && <MortgageCalc />}
        {tab === "vendors"   && <VendorsTab />}
        {tab === "resources" && <ResourcesTab />}
        {tab === "documents" && <TabRedirect to="/documents" label="Documents" desc="Upload disclosures, lender paperwork, and everything else in one shared folder." />}
        {tab === "forms"     && <Forms />}
        {tab === "ask" && <TabRedirect to="/ask" label={`Ask ${agentFirst}`} desc={`Send a message directly to ${agentFirst}. They reply from their admin inbox.`} />}
        {tab === "refer"     && <ReferTab />}
        {tab === "timeline"  && <ContractTimelineTab />}
      </main>
    </div>
  );
}

/* ---------- Dashboard tab ---------- */
function DashboardTab({ goto }) {
  const { agent_first_name: agentFirst } = useBranding();
  const { user } = useAuth();
  const { content } = useContent();
  const firstName = (user?.name || "").split(" ")[0] || "there";
  const stages = content?.buyer_timeline || DEFAULT_CONTENT.buyer_timeline || [];
  const stageCount = stages.length;
  const dash = content?.buyer_dashboard || DEFAULT_CONTENT.buyer_dashboard;
  const qa = (dash.quick_access && dash.quick_access.length === 4) ? dash.quick_access : DEFAULT_CONTENT.buyer_dashboard.quick_access;
  const [subs, setSubs] = useState(null);
  const [agentNote, setAgentNote] = useState("");
  const [pers, setPers] = useState(null);

  useEffect(() => {
    api.get("/forms/my-submissions")
      .then((r) => setSubs(r.data.submissions || []))
      .catch(() => setSubs([]));
    api.get("/me/personalization")
      .then((r) => { const p = r.data?.personalization || {}; setPers(p); setAgentNote(p.agent_note || ""); })
      .catch(() => {});
  }, []);

  const kd = pers?.key_dates || {};
  const uc = pers?.under_contract;
  const closingRaw = (() => {
    const entries = Object.entries(kd).filter(([, v]) => v);
    const closingKey = entries.find(([k]) => /clos/i.test(k));
    if (closingKey) return closingKey[1];
    const dates = entries.map(([, v]) => v).filter((v) => !isNaN(Date.parse(v))).sort();
    return dates.length ? dates[dates.length - 1] : null;
  })();
  const underContract = !!(uc && (typeof uc === "object" ? Object.keys(uc).length : uc)) || !!closingRaw;
  const daysToClose = closingRaw && !isNaN(Date.parse(closingRaw))
    ? Math.ceil((new Date(closingRaw) - new Date()) / 86400000) : null;

  const intakeDone = !!subs?.some((s) => s.form_type === "buyer_intake");
  const prefsDone = !!subs?.some((s) => s.form_type === "buyer_preferences");
  const submitted = (intakeDone ? 1 : 0) + (prefsDone ? 1 : 0);
  const pct = Math.round((submitted / 2) * 100);
  const allDone = submitted === 2;
  const nextFormLabel = !intakeDone ? "get to know you" : (!prefsDone ? "what you're looking for" : null);

  return (
    <div data-testid="buyer-dashboard-home">
      <span className="overline">Your dashboard</span>
      <h2 className="font-serif" style={{ fontSize: "clamp(2.4rem, 5vw, 3.6rem)", fontWeight: 500, letterSpacing: "-0.02em", margin: "8px 0 14px", lineHeight: 1.05 }}>
        Hello, {firstName}.
      </h2>
      <p style={{ color: "var(--text-muted)", maxWidth: 640, margin: "0 0 40px", lineHeight: 1.6 }}>
        {dash.subtitle}
      </p>

      {agentNote && (
        <div data-testid="dash-agent-note" style={{ background: "var(--surface-alt)", borderLeft: "3px solid var(--primary)", borderRadius: "0 10px 10px 0", padding: "20px 24px", marginBottom: 40 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <Mail size={14} color="var(--primary)" /><span className="overline">A note from {agentFirst}</span>
          </div>
          <p className="font-serif" style={{ fontStyle: "italic", fontSize: 18, lineHeight: 1.5, margin: 0, color: "var(--text)", whiteSpace: "pre-wrap" }}>{agentNote}</p>
        </div>
      )}

      {/* Status hero cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 18, marginBottom: 56 }}>
        {/* Intake forms progress */}
        <button type="button" onClick={() => goto("forms")} data-testid="dash-card-forms" className="card card-hover"
          style={{ textAlign: "left", cursor: "pointer", padding: 28, background: "var(--surface)", display: "flex", alignItems: "center", gap: 22 }}>
          <ProgressRing pct={pct} />
          <div>
            <div style={{ borderTop: "1px solid var(--text)", width: 26, marginBottom: 8 }} />
            <span className="overline" style={{ fontSize: 10 }}>{dash.card_forms_label || "Intake forms"}</span>
            <div className="font-serif" style={{ fontSize: 26, fontWeight: 500, letterSpacing: "-0.01em", margin: "4px 0 8px", lineHeight: 1.1 }}>{submitted} / 2 submitted</div>
            <span style={{ color: "var(--primary)", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 }}>
              {allDone ? "Review" : "Continue"} <ArrowRight size={14} />
            </span>
          </div>
        </button>

        {/* Buyer process — flips to Contract timeline when under contract */}
        <button type="button" onClick={() => goto(underContract ? "timeline" : "process")} data-testid="dash-card-process" className="card card-hover"
          style={{ textAlign: "left", cursor: "pointer", padding: 28, background: "var(--surface)", display: "flex", alignItems: "center", gap: 22 }}>
          <div style={{ width: 84, height: 84, borderRadius: "50%", background: "var(--surface-alt)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {underContract ? <Calendar size={30} color="var(--primary)" strokeWidth={1.4} /> : <Compass size={30} color="var(--primary)" strokeWidth={1.4} />}
          </div>
          <div>
            <div style={{ borderTop: "1px solid var(--text)", width: 26, marginBottom: 8 }} />
            <span className="overline" style={{ fontSize: 10 }}>{underContract ? "Contract timeline" : (dash.card_process_label || "Buyer process")}</span>
            <div className="font-serif" style={{ fontSize: 26, fontWeight: 500, letterSpacing: "-0.01em", margin: "4px 0 8px", lineHeight: 1.1 }}>
              {underContract
                ? (daysToClose != null && daysToClose >= 0 ? `${daysToClose} days to closing` : "You're under contract")
                : `${stageCount} stages to keys`}
            </div>
            <span style={{ color: "var(--primary)", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 }}>{underContract ? "Open timeline" : "See the process"} <ArrowRight size={14} /></span>
          </div>
        </button>

        {/* Up next */}
        <div className="card" data-testid="dash-card-upnext" style={{ padding: 28, background: "var(--surface)" }}>
          <div style={{ borderTop: "1px solid var(--text)", width: 26, marginBottom: 8 }} />
          <span className="overline" style={{ fontSize: 10 }}>{dash.card_upnext_label || "Up next"}</span>
          {allDone ? (
            <>
              <div className="font-serif" style={{ fontSize: 23, fontWeight: 500, letterSpacing: "-0.01em", margin: "6px 0 8px", lineHeight: 1.15 }}>{dash.alldone_title}</div>
              <p style={{ color: "var(--text-muted)", fontSize: 14, lineHeight: 1.55, margin: "0 0 14px" }}>{dash.alldone_note}</p>
              <button type="button" onClick={() => goto("process")} className="btn-ghost" data-testid="dash-upnext-cta" style={{ padding: 0, color: "var(--primary)", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 }}>See the process <ArrowRight size={14} /></button>
            </>
          ) : (
            <>
              <div className="font-serif" style={{ fontSize: 23, fontWeight: 500, letterSpacing: "-0.01em", margin: "6px 0 8px", lineHeight: 1.15 }}>Finish your {nextFormLabel} form</div>
              <p style={{ color: "var(--text-muted)", fontSize: 14, lineHeight: 1.55, margin: "0 0 14px" }}>{dash.upnext_note}</p>
              <button type="button" onClick={() => goto("forms")} className="btn-ghost" data-testid="dash-upnext-cta" style={{ padding: 0, color: "var(--primary)", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 }}>Go to Forms <ArrowRight size={14} /></button>
            </>
          )}
        </div>
      </div>

      {/* Quick access */}
      <span className="overline">Quick access</span>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginTop: 20 }}>
        <QuickLink icon={Compass} title={qa[0].title} sub={qa[0].sub} onClick={() => goto("process")} testid="quick-process" />
        <QuickLink icon={Calculator} title={qa[1].title} sub={qa[1].sub} onClick={() => goto("calc")} testid="quick-calc" />
        <QuickLink icon={ClipboardList} title={qa[2].title} sub={qa[2].sub} onClick={() => goto("forms")} testid="quick-forms" />
        <QuickLink icon={Users} title={qa[3].title} sub={qa[3].sub} onClick={() => goto("vendors")} testid="quick-vendors" />
      </div>

      {subs && subs.length > 0 && (
        <div data-testid="dash-recent-submissions" style={{ marginTop: 56 }}>
          <span className="overline">Recent submissions</span>
          <div className="card" style={{ marginTop: 16, padding: 0, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ color: "var(--text-muted)", textAlign: "left", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  <th style={{ padding: "14px 22px" }}>Form</th>
                  <th style={{ padding: "14px 22px" }}>Submitted</th>
                  <th style={{ padding: "14px 22px", textAlign: "right" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s, i) => (
                  <tr key={s.submission_id || i} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={{ padding: "16px 22px", fontFamily: "var(--font-serif)", fontSize: 16 }}>{FORM_TITLES[s.form_type] || s.title || s.form_type}</td>
                    <td style={{ padding: "16px 22px", color: "var(--text-muted)" }}>{s.created_at ? new Date(s.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                    <td style={{ padding: "16px 22px", textAlign: "right" }}>
                      <span style={{ background: "#E7F0EA", color: "#2B7A4B", padding: "4px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em" }}>RECEIVED</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const FORM_TITLES = {
  buyer_intake: "Get to know you",
  buyer_preferences: "What you're looking for",
};

function ProgressRing({ pct }) {
  const r = 34, c = 2 * Math.PI * r, off = c - (pct / 100) * c;
  return (
    <div style={{ position: "relative", width: 84, height: 84, flexShrink: 0 }}>
      <svg width="84" height="84" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="42" cy="42" r={r} fill="none" stroke="var(--surface-alt)" strokeWidth="6" />
        <circle cx="42" cy="42" r={r} fill="none" stroke="var(--primary)" strokeWidth="6" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.5s" }} />
      </svg>
      <span className="font-serif" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, fontWeight: 500 }}>{pct}%</span>
    </div>
  );
}

function QuickLink({ icon: Icon, title, sub, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="card card-hover"
      style={{ textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", gap: 16, padding: 22, background: "var(--surface)", border: "1px solid var(--border)" }}
    >
      <div style={{ width: 44, height: 44, background: "var(--surface-alt)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={20} color="var(--primary)" strokeWidth={1.6} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="font-serif" style={{ fontSize: 19, fontWeight: 500, letterSpacing: "-0.01em", lineHeight: 1.2 }}>{title}</div>
        <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>{sub}</div>
      </div>
    </button>
  );
}

/* ---------- Buyer Process tab (reads buyer_timeline, per-buyer override aware) ---------- */
function ProcessTab() {
  const { buyerTimeline: stages } = useBuyerContent();
  return (
    <div data-testid="buyer-process-tab">
      <span className="overline">Buyer process</span>
      <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: 500, letterSpacing: "-0.01em", margin: "8px 0 12px", lineHeight: 1.1 }}>
        From pre-approval to keys.
      </h2>
      <p style={{ color: "var(--text-muted)", maxWidth: 620, margin: "0 0 40px", lineHeight: 1.6 }}>
        Every step of a home purchase, in order. Take it one stage at a time.
      </p>
      <div>
        {stages.map((s, i) => (
          <div key={s.id || i} data-testid={`process-stage-${i}`} className="timeline-item" style={{ position: "relative", paddingLeft: 36, paddingBottom: 28, borderLeft: i === stages.length - 1 ? "1px solid transparent" : "1px solid var(--border)" }}>
            <div style={{ position: "absolute", left: -7, top: 4, width: 13, height: 13, borderRadius: "50%", background: "var(--surface)", border: "2px solid var(--primary)" }} />
            <span className="overline" style={{ fontSize: 10 }}>Stage {String(i + 1).padStart(2, "0")}</span>
            <h3 className="font-serif" style={{ fontSize: 22, fontWeight: 500, letterSpacing: "-0.01em", margin: "4px 0 8px" }}>{s.title}</h3>
            {(s.summary || s.blurb) && <p style={{ color: "var(--text)", lineHeight: 1.55, margin: "0 0 8px", fontSize: 15 }}>{s.summary || s.blurb}</p>}
            {s.details && <p style={{ color: "var(--text-muted)", lineHeight: 1.55, margin: 0, fontSize: 14 }}>{s.details}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Lender Info tab (reads content.lender, per-buyer override aware) ---------- */
function LenderInfoTab() {
  const { agent_first_name: agentFirst } = useBranding();
  const { lender: lo } = useBuyerContent();
  return (
    <div data-testid="buyer-lender-tab">
      <span className="overline">Lender info</span>
      <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: 500, letterSpacing: "-0.01em", margin: "8px 0 12px", lineHeight: 1.1 }}>
        Meet your preferred lender.
      </h2>
      <p style={{ color: "var(--text-muted)", maxWidth: 620, margin: "0 0 32px", lineHeight: 1.6 }}>
        Getting pre-approved is the first real step. Here&rsquo;s who {agentFirst} recommends and how to reach them.
      </p>
      <div className="card" style={{ padding: 32, background: "var(--surface)" }}>
        <span className="overline">Loan Officer</span>
        <div className="font-serif" style={{ fontSize: 32, fontWeight: 500, margin: "10px 0 4px", letterSpacing: "-0.01em" }}>{lo.name}</div>
        <div style={{ color: "var(--text-muted)", fontSize: 14 }}>
          {lo.title}{lo.title && lo.company ? " · " : ""}{lo.company}
          {lo.nmls ? ` · NMLS ${lo.nmls}` : ""}
        </div>
        <div style={{ display: "flex", gap: 22, flexWrap: "wrap", marginTop: 26, fontSize: 15 }}>
          {lo.phone && <a href={`tel:${lo.phone}`} data-testid="lender-phone-link" style={{ color: "var(--primary)", display: "inline-flex", gap: 8, alignItems: "center" }}><Phone size={16} /> {lo.phone}</a>}
          {lo.email && <a href={`mailto:${lo.email}`} data-testid="lender-email-link" style={{ color: "var(--primary)", display: "inline-flex", gap: 8, alignItems: "center" }}><Mail size={16} /> {lo.email}</a>}
        </div>
      </div>
    </div>
  );
}

/* ---------- Vendors tab ---------- */
function VendorsTab() {
  const { agent_first_name: agentFirst } = useBranding();
  const { content } = useContent();
  const vendors = content?.vendors || DEFAULT_CONTENT.vendors || [];
  return (
    <div data-testid="buyer-vendors-tab">
      <span className="overline">Vendors</span>
      <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: 500, letterSpacing: "-0.01em", margin: "8px 0 12px", lineHeight: 1.1 }}>
        Trusted local pros.
      </h2>
      <p style={{ color: "var(--text-muted)", maxWidth: 620, margin: "0 0 32px", lineHeight: 1.6 }}>
        Inspectors, insurance agents, movers &mdash; the people {agentFirst} has worked with and trusts.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        {vendors.length === 0 ? (
          <div className="card" style={{ color: "var(--text-muted)", fontSize: 14 }}>No vendors listed yet.</div>
        ) : vendors.map((v, i) => (
          <div key={v.id || i} className="card" data-testid={`vendor-card-${i}`} style={{ padding: 24, background: "var(--surface)" }}>
            <span className="overline">{v.category || "Trusted vendor"}</span>
            <div className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: "8px 0 4px", letterSpacing: "-0.01em" }}>{v.name}</div>
            {v.blurb && <div style={{ color: "var(--text-muted)", fontSize: 14, lineHeight: 1.55, marginBottom: 12 }}>{v.blurb}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 14 }}>
              {v.phone && <a href={`tel:${v.phone}`} style={{ color: "var(--primary)", display: "inline-flex", gap: 6, alignItems: "center" }}><Phone size={13} /> {v.phone}</a>}
              {v.email && <a href={`mailto:${v.email}`} style={{ color: "var(--primary)", display: "inline-flex", gap: 6, alignItems: "center" }}><Mail size={13} /> {v.email}</a>}
              {v.website && <a href={v.website} target="_blank" rel="noreferrer" style={{ color: "var(--primary)" }}>{v.website.replace(/^https?:\/\//, "")}</a>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Resources tab ---------- */
function ResourcesTab() {
  const { agent_first_name: agentFirst } = useBranding();
  const { content } = useContent();
  const items = content?.buyer_resources || DEFAULT_CONTENT.buyer_resources || [];
  return (
    <div data-testid="buyer-resources-tab">
      <span className="overline">Resources</span>
      <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: 500, letterSpacing: "-0.01em", margin: "8px 0 12px", lineHeight: 1.1 }}>
        Reading &amp; guides.
      </h2>
      <p style={{ color: "var(--text-muted)", maxWidth: 620, margin: "0 0 32px", lineHeight: 1.6 }}>
        Handpicked articles and videos on getting pre-approved, negotiating an offer, and what to expect at closing.
      </p>
      {items.length === 0 ? (
        <div className="card" style={{ padding: 24, background: "var(--surface-alt)", color: "var(--text-muted)" }}>
          <BookOpen size={20} style={{ marginBottom: 10, color: "var(--primary)" }} />
          <p style={{ margin: 0 }}>Resources are being curated. Ask {agentFirst} for links to specific topics in the meantime.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
          {items.map((r, i) => (
            <a
              key={r.id || i}
              href={r.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`resource-${i}`}
              className="card card-hover"
              style={{ padding: 24, background: "var(--surface)", textDecoration: "none", color: "var(--text)", display: "flex", flexDirection: "column", gap: 8 }}
            >
              <BookOpen size={18} color="var(--primary)" />
              <div className="font-serif" style={{ fontSize: 20, fontWeight: 500, letterSpacing: "-0.01em" }}>{r.title}</div>
              {r.description && <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 14, lineHeight: 1.55 }}>{r.description}</p>}
              <span style={{ color: "var(--primary)", fontSize: 13, marginTop: "auto", paddingTop: 8, display: "inline-flex", alignItems: "center", gap: 4 }}>
                Open <ArrowRight size={12} />
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Refer a Friend tab ---------- */
function ReferTab() {
  const { agent_first_name: agentFirst } = useBranding();
  const [form, setForm] = useState({ friend_name: "", friend_email: "", note: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (status.state === "sending") return;
    setStatus({ state: "sending", msg: "" });
    try {
      await api.post("/referrals", form);
      setStatus({ state: "success", msg: `Thanks, ${agentFirst} will reach out to your friend.` });
      setForm({ friend_name: "", friend_email: "", note: "" });
    } catch (err) {
      setStatus({ state: "error", msg: err?.response?.data?.detail || "Couldn't send that referral. Try again?" });
    }
  };
  return (
    <div data-testid="buyer-refer-tab" style={{ maxWidth: 640 }}>
      <span className="overline">Refer a friend</span>
      <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: 500, letterSpacing: "-0.01em", margin: "8px 0 12px", lineHeight: 1.1 }}>
        Know someone else looking?
      </h2>
      <p style={{ color: "var(--text-muted)", margin: "0 0 32px", lineHeight: 1.6 }}>
        Send {agentFirst} their name and email and they&rsquo;ll reach out personally. No pressure &mdash; just a warm intro.
      </p>
      <form onSubmit={submit} className="card" style={{ padding: 28, background: "var(--surface)", display: "grid", gap: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 500 }}>
          Friend&rsquo;s name
          <input required data-testid="refer-friend-name" className="input-field" style={{ marginTop: 6 }} value={form.friend_name} onChange={upd("friend_name")} placeholder="Jamie Buyer" />
        </label>
        <label style={{ fontSize: 13, fontWeight: 500 }}>
          Friend&rsquo;s email
          <input required type="email" data-testid="refer-friend-email" className="input-field" style={{ marginTop: 6 }} value={form.friend_email} onChange={upd("friend_email")} placeholder="jamie@email.com" />
        </label>
        <label style={{ fontSize: 13, fontWeight: 500 }}>
          Note to {agentFirst} <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(optional)</span>
          <textarea data-testid="refer-note" className="input-field" style={{ marginTop: 6, minHeight: 80 }} value={form.note} onChange={upd("note")} placeholder="They just got pre-approved and are looking in Draper." />
        </label>
        <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <button type="submit" className="btn-primary" disabled={status.state === "sending"} data-testid="refer-submit-btn">
            {status.state === "sending" ? "Sending…" : <>Send referral <Send size={14} /></>}
          </button>
          {status.state === "success" && <span data-testid="refer-success" style={{ color: "var(--primary)", fontSize: 14 }}>{status.msg}</span>}
          {status.state === "error"   && <span data-testid="refer-error" style={{ color: "#a14040", fontSize: 14 }}>{status.msg}</span>}
        </div>
      </form>
    </div>
  );
}

/* ---------- Contract Timeline tab (reads buyer key_dates via personalization) ---------- */
function ContractTimelineTab() {
  const { agent_first_name: agentFirst } = useBranding();
  const { content } = useContent();
  const { buyerClosing } = useBuyerContent();
  const [dates, setDates] = useState({});
  const [checked, setChecked] = useState(new Set());
  const toggle = (id) => setChecked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  useEffect(() => {
    api.get("/me/personalization").then((r) => setDates(r.data?.personalization?.key_dates || {})).catch(() => {});
  }, []);
  const fields = content?.buyer_date_fields || DEFAULT_CONTENT.buyer_date_fields || [];
  const populated = fields.filter((f) => dates[f.key]);
  const upcoming = useMemo(() => populated
    .map((f) => ({ ...f, iso: dates[f.key] }))
    .sort((a, b) => new Date(a.iso) - new Date(b.iso)), [populated, dates]);
  return (
    <div data-testid="buyer-timeline-tab">
      <span className="overline">Contract timeline</span>
      <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: 500, letterSpacing: "-0.01em", margin: "8px 0 12px", lineHeight: 1.1 }}>
        Every key date, in order.
      </h2>
      <p style={{ color: "var(--text-muted)", maxWidth: 620, margin: "0 0 32px", lineHeight: 1.6 }}>
        Once you&rsquo;re under contract, {agentFirst} fills in the dates that matter &mdash; from earnest money wire to closing day.
      </p>
      {upcoming.length === 0 ? (
        <div className="card" style={{ background: "var(--surface-alt)", color: "var(--text-muted)", padding: 24, fontSize: 14 }}>
          No dates yet. When you go under contract, {agentFirst} will start filling these in and they&rsquo;ll appear here in order.
        </div>
      ) : (
        <div>
          {upcoming.map((f, i) => (
            <div key={f.key} data-testid={`timeline-row-${f.key}`} className="timeline-item" style={{ position: "relative", paddingLeft: 36, paddingBottom: 22, borderLeft: i === upcoming.length - 1 ? "1px solid transparent" : "1px solid var(--border)" }}>
              <div style={{ position: "absolute", left: -7, top: 4, width: 13, height: 13, borderRadius: "50%", background: "var(--surface)", border: "2px solid var(--primary)" }} />
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                <span className="font-serif" style={{ fontSize: 20, fontWeight: 500 }}>{f.label}</span>
                <span style={{ color: "var(--text-muted)", fontSize: 14 }}>{new Date(f.iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</span>
              </div>
              {f.hint && <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 0" }}>{f.hint}</p>}
            </div>
          ))}
        </div>
      )}

      {Array.isArray(buyerClosing) && buyerClosing.length > 0 && (
        <div style={{ marginTop: 44 }} data-testid="buyer-closing-checklist">
          <span className="overline">Closing day</span>
          <h3 className="font-serif" style={{ fontSize: "clamp(1.4rem, 3vw, 1.9rem)", fontWeight: 500, margin: "6px 0 16px" }}>A few things for closing day.</h3>
          <div className="card" style={{ padding: 28, maxWidth: 720 }}>
            {buyerClosing.map((item) => {
              const isChecked = checked.has(item.id);
              return (
                <div key={item.id} className={`check-item ${isChecked ? "checked" : ""}`} onClick={() => toggle(item.id)} data-testid={`buyer-closing-item-${item.id}`} role="button">
                  <div className="check-box">{isChecked && <Check size={14} color="#fff" strokeWidth={3} />}</div>
                  <div className="check-label">{item.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
/* ---------- Home Wishlist tab (buyer saves toured homes) ---------- */
const WL_STATUS = {
  loved:  { label: "Loved it", color: "#2B7A4B", bg: "#E7F0EA" },
  maybe:  { label: "Maybe",    color: "#B8860B", bg: "#F6EFD9" },
  passed: { label: "Passed",   color: "#9A4A3A", bg: "#F5E4E0" },
};

function WishlistPhoto({ itemId, photoId, mime, onDelete }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true; let objUrl = null;
    api.get(`/wishlist/photo/${photoId}`, { responseType: "blob" })
      .then((r) => { if (!alive) return; objUrl = URL.createObjectURL(r.data); setUrl(objUrl); })
      .catch(() => {});
    return () => { alive = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [photoId]);
  return (
    <div data-testid={`wishlist-photo-${photoId}`} style={{ position: "relative", width: 92, height: 92, borderRadius: 10, overflow: "hidden", background: "var(--surface-alt)", border: "1px solid var(--border)", flexShrink: 0 }}>
      {url ? <img src={url} alt="toured home" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%" }} />}
      {onDelete && (
        <button type="button" onClick={() => onDelete(photoId)} data-testid={`wishlist-photo-remove-${photoId}`}
          style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%", border: "none", background: "rgba(28,25,23,0.7)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={13} />
        </button>
      )}
    </div>
  );
}

const EMPTY_HOME = { title: "", address: "", notes: "", status: "maybe" };

function WishlistTab() {
  const { agent_first_name: agentFirst } = useBranding();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_HOME);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/me/wishlist"); setItems(data.items || []); }
    catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY_HOME); setEditingId(null); setShowForm(true); };
  const openEdit = (it) => { setForm({ title: it.title, address: it.address, notes: it.notes, status: it.status }); setEditingId(it.item_id); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (editingId) await api.put(`/me/wishlist/${editingId}`, form);
      else await api.post("/me/wishlist", form);
      setShowForm(false); setForm(EMPTY_HOME); setEditingId(null);
      await load();
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this home from your wishlist?")) return;
    try { await api.delete(`/me/wishlist/${id}`); await load(); } catch {}
  };

  const setStatus = async (it, status) => {
    try { await api.put(`/me/wishlist/${it.item_id}`, { title: it.title, address: it.address, notes: it.notes, status }); await load(); } catch {}
  };

  const uploadPhoto = async (itemId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/me/wishlist/${itemId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); await load(); }
    catch (err) { alert(err?.response?.data?.detail || "Couldn't upload that photo."); }
  };

  const removePhoto = async (itemId, photoId) => {
    try { await api.delete(`/me/wishlist/${itemId}/photo/${photoId}`); await load(); } catch {}
  };

  return (
    <div data-testid="buyer-wishlist-tab">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap", marginBottom: 28 }}>
        <div>
          <span className="overline">Home wishlist</span>
          <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: 500, letterSpacing: "-0.01em", margin: "8px 0 12px", lineHeight: 1.1 }}>
            Homes you&rsquo;ve toured.
          </h2>
          <p style={{ color: "var(--text-muted)", maxWidth: 620, margin: 0, lineHeight: 1.6 }}>
            Save every home you visit with your notes and photos. Mark it Loved it, Maybe, or Passed &mdash; {agentFirst} sees your shortlist so they can find you the one.
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={openAdd} data-testid="wishlist-add-btn">
          <Plus size={15} /> Add a home
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="card" data-testid="wishlist-form" style={{ padding: 26, marginBottom: 28, background: "var(--surface)", display: "grid", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>
              Nickname
              <input required data-testid="wishlist-title" className="input-field" style={{ marginTop: 6 }} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="The one with the big yard" />
            </label>
            <label style={{ fontSize: 13, fontWeight: 500 }}>
              Address
              <input data-testid="wishlist-address" className="input-field" style={{ marginTop: 6 }} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Maple St, Draper" />
            </label>
          </div>
          <label style={{ fontSize: 13, fontWeight: 500 }}>
            Your notes
            <textarea data-testid="wishlist-notes" className="input-field" style={{ marginTop: 6, minHeight: 90 }} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Loved the kitchen, but the backyard is small. Master bath needs work." />
          </label>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>How do you feel about it?</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {Object.entries(WL_STATUS).map(([key, s]) => (
                <button key={key} type="button" data-testid={`wishlist-status-pick-${key}`} onClick={() => setForm({ ...form, status: key })}
                  style={{ padding: "8px 16px", borderRadius: 999, cursor: "pointer", fontSize: 13, fontWeight: 600,
                    border: `1px solid ${form.status === key ? s.color : "var(--border)"}`,
                    background: form.status === key ? s.bg : "transparent", color: form.status === key ? s.color : "var(--text-muted)" }}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button type="submit" className="btn-primary" disabled={busy} data-testid="wishlist-save-btn">{busy ? "Saving…" : (editingId ? "Save changes" : "Add home")}</button>
            <button type="button" className="btn-ghost" onClick={() => { setShowForm(false); setEditingId(null); }} data-testid="wishlist-cancel-btn">Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="card" style={{ padding: 24, color: "var(--text-muted)" }}>Loading your homes…</div>
      ) : items.length === 0 ? (
        <div className="card" data-testid="wishlist-empty" style={{ padding: 40, textAlign: "center", background: "var(--surface-alt)" }}>
          <Heart size={26} color="var(--primary)" style={{ marginBottom: 12 }} />
          <div className="font-serif" style={{ fontSize: 22, fontWeight: 500, marginBottom: 6 }}>No homes saved yet.</div>
          <p style={{ color: "var(--text-muted)", margin: "0 auto", maxWidth: 420, lineHeight: 1.6 }}>
            After each showing, tap &ldquo;Add a home&rdquo; to jot down what you loved (or didn&rsquo;t) and add a few photos.
          </p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 18 }}>
          {items.map((it) => {
            const s = WL_STATUS[it.status] || WL_STATUS.maybe;
            return (
              <div key={it.item_id} className="card" data-testid={`wishlist-item-${it.item_id}`} style={{ padding: 22, background: "var(--surface)", display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="font-serif" style={{ fontSize: 21, fontWeight: 500, letterSpacing: "-0.01em", lineHeight: 1.2 }}>{it.title || "Untitled home"}</div>
                    {it.address && <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4, display: "inline-flex", gap: 6, alignItems: "center" }}><Home size={13} /> {it.address}</div>}
                  </div>
                  <span data-testid={`wishlist-status-${it.item_id}`} style={{ flexShrink: 0, padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: s.bg, color: s.color }}>{s.label}</span>
                </div>

                {it.notes && <p style={{ color: "var(--text)", fontSize: 14, lineHeight: 1.55, margin: 0, whiteSpace: "pre-wrap" }}>{it.notes}</p>}

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {(it.photos || []).map((p) => (
                    <WishlistPhoto key={p.photo_id} itemId={it.item_id} photoId={p.photo_id} mime={p.mime} onDelete={(pid) => removePhoto(it.item_id, pid)} />
                  ))}
                  <label data-testid={`wishlist-photo-add-${it.item_id}`} style={{ width: 92, height: 92, borderRadius: 10, border: "1px dashed var(--border)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, cursor: "pointer", color: "var(--text-muted)", fontSize: 11 }}>
                    <Camera size={18} />
                    Add photo
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { uploadPhoto(it.item_id, e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                </div>

                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 12 }}>
                  <span style={{ fontSize: 12, color: "var(--text-muted)", marginRight: 4 }}>Set:</span>
                  {Object.entries(WL_STATUS).map(([key, st]) => (
                    <button key={key} type="button" data-testid={`wishlist-set-${key}-${it.item_id}`} onClick={() => setStatus(it, key)}
                      style={{ padding: "5px 12px", borderRadius: 999, cursor: "pointer", fontSize: 12, fontWeight: 600,
                        border: `1px solid ${it.status === key ? st.color : "var(--border)"}`,
                        background: it.status === key ? st.bg : "transparent", color: it.status === key ? st.color : "var(--text-muted)" }}>
                      {st.label}
                    </button>
                  ))}
                  <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                    <button type="button" className="btn-ghost" style={{ padding: 8 }} onClick={() => openEdit(it)} data-testid={`wishlist-edit-${it.item_id}`}><Pencil size={14} /></button>
                    <button type="button" className="btn-ghost" style={{ padding: 8, color: "#9A4A3A" }} onClick={() => remove(it.item_id)} data-testid={`wishlist-remove-${it.item_id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------- Simple placeholder that links out to an existing PorchLight route ---------- */
function TabRedirect({ to, label, desc }) {
  const nav = useNavigate();
  useEffect(() => { const t = setTimeout(() => nav(to), 300); return () => clearTimeout(t); }, [to, nav]);
  return (
    <div data-testid={`tab-redirect-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="overline">{label}</span>
      <h2 className="font-serif" style={{ fontSize: "2rem", fontWeight: 500, margin: "8px 0 12px" }}>{label}</h2>
      <p style={{ color: "var(--text-muted)", maxWidth: 560 }}>{desc}</p>
      <a href={to} className="btn-primary" style={{ marginTop: 16 }} data-testid={`goto-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        Open {label} <ArrowRight size={14} />
      </a>
    </div>
  );
}
